import { InteractionStatus } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  AUTH_TYPE_KEY,
  AUTH_TYPES,
  authStorage,
  refreshTokenHandler,
} from '@brightdrop/auth-client';
import { BDPageSpinner } from '@brightdrop/bd-ui';
import {
  useFeatureFlags,
  useFeatureFlagsProviderContext,
} from '@brightdrop/feature-flags-client';
import { TelemetryProviderContext } from '@brightdrop/telemetry-client';
import { fetchEntitlements } from '@gm-commercial/entitlements-client/src/state/entitlementsSlice';
import { InvitationStatus, Role } from '@gm-commercial/profile-model';
import { rtkOrgClient } from '@gm-commercial/rtk-clients';
import { Box } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { skipToken } from '@reduxjs/toolkit/query/react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '~/app/store';
import { useUser } from '~/common/contexts/UserContext';
import usePageTitle from '~/common/hooks/usePageTitle';
import {
  BDRequestStatus,
  BDRequestType,
} from '~/common/models/apis/apiResponse.model';
import { ActivationStatus } from '~/common/models/common.model';
import type { BDError } from '~/common/models/error.model';
import { ADVISOR_TOOL_GMF_FLAG } from '~/common/models/featureFlags.model';
import { SESSION_START_EVENT_BY_PLATFORM } from '~/common/models/heap.model';
import { AppRoutePaths, RouteName } from '~/common/models/route.model';
import {
  buildHeapUserProperties,
  dispatchHeapEvent,
  getHeapPlatform,
} from '~/common/utils/analytics/heap.utils';
import {
  LoginRead,
  LoginReadOutcome,
  logLoginReadDecision,
} from '~/common/utils/analytics/login-telemetry';
import { hasPrematureEnrollmentSession } from '~/common/utils/enrollment.utils';
import { sendTokenToNative } from '~/common/utils/native/native.utils';
import {
  getGmfAdvisorDestination,
  isGmfRolloutEnabled,
} from '~/common/utils/route/gmfAdvisorPolicy';
import {
  findRoute,
  getDefaultRoutePath,
  getImpersonationHomePath,
} from '~/common/utils/route/route.utils';
import { getOrganizationDetails } from '~/features/organizations/organizationsSlice';
import { makeSelectOrgOperationByType } from '~/features/organizations/organizationsSlice.selectors';
import { setEffectiveFeatures } from '~/store/featureFlags/featureFlagsSlice';
import {
  getActingOrgId,
  hasPersistedActingOrgContext,
} from '~/store/profile/actingProfile.utils';
import {
  getAllowedContexts,
  ProfileRequestType,
  setActingProfile,
} from '~/store/profile/profileSlice';
import {
  makeProfileOperationSelector,
  selectActingProfile,
  selectAllowedContexts,
  selectCurrentLocale,
  selectPermissionsContextIds,
  selectProfile,
  selectProfileContextIds,
  selectProfileFleet,
  selectProfileHub,
  selectProfileId,
  selectProfileInvite,
  selectProfileOrganization,
  selectProfileStatus,
  selectRole,
} from '~/store/profile/profileSlice.selectors';
import { buildRedirectPathQuery } from '~/utils/auth/location.utils';

export enum BDLoginError {
  NO_PROFILE = 'no-profile',
  SERVICE_ERROR = 'service-unavailable',
}

const CONTEXT_EXCLUSIONS = [Role.SYSTEM_MANAGER, Role.GMF_ADVISOR];

/**
 * Full path to the Business Enrollment "Pending Verification" screen. The
 * enrollment micro-frontend is mounted under `BUSINESS_ENROLLMENT`, so the
 * pending-verification route lives beneath it.
 */
const PENDING_VERIFICATION_PATH = `${AppRoutePaths.BUSINESS_ENROLLMENT}/pending-verification`;

/**
 * Organization statuses that indicate the user has already submitted an
 * enrollment that is awaiting verification, so they should land on the pending
 * verification screen rather than restarting enrollment.
 */
const PENDING_ENROLLMENT_ORG_STATUSES = ['PENDING', 'ENROLLING'];

const selectProfileParams = createSelector(
  selectProfileId,
  selectRole,
  selectProfileStatus,
  selectProfileOrganization,
  selectProfileHub,
  selectProfileFleet,
  selectProfileInvite,
  (id, role, status, organization, hub, fleet, invite) => {
    return { id, role, status, organization, hub, fleet, invite };
  }
);

const selectGetProfileOperation = makeProfileOperationSelector(
  ProfileRequestType.GET_PROFILE
);
const selectGetPermissionsOperation = makeProfileOperationSelector(
  ProfileRequestType.GET_PERMISSIONS
);
const selectGetOrgDetailsOperation = makeSelectOrgOperationByType(
  BDRequestType.GET_BY_ID
);

const PAGE_NAME = 'Login Inbound';

const InboundRedirect = (): JSX.Element | null => {
  usePageTitle(PAGE_NAME);

  const dispatch = useAppDispatch();
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  const navigate = useNavigate();
  const location = useLocation();
  const { featureFlags, getFlag } = useFeatureFlags();

  const redirectPath = location.state?.SecureRoute?.pathname;
  const redirectSearch = location.state?.SecureRoute?.search;
  const redirectHash = location.state?.SecureRoute?.hash;
  const hasAnyCachedAccounts = (instance?.getAllAccounts()?.length ?? 0) > 0;
  const isMsalInitialized = inProgress === InteractionStatus.None;

  const previousRoute = useMemo(
    () => buildRedirectPathQuery(location.state?.SecureRoute),
    [location.state?.SecureRoute]
  );

  /**
   * The deep link this bootstrap is resuming, if any: either the location a
   * `SecureRoute` bounced through here or a `redirectPath` left on the query.
   * Null means the user landed on the base URL with nowhere specific to go.
   */
  const pendingRedirectPath = useMemo(() => {
    if (redirectPath && redirectPath !== '/') {
      return `${redirectPath}${redirectSearch ?? ''}${redirectHash ?? ''}`;
    }

    const queryRedirectPath = new URLSearchParams(location.search).get(
      'redirectPath'
    );
    return queryRedirectPath && queryRedirectPath !== '/'
      ? queryRedirectPath
      : null;
  }, [redirectPath, redirectSearch, redirectHash, location.search]);

  const { addTelemetryContext } = useContext(TelemetryProviderContext);
  /**
   * Wait until the latest LD `identify` from `FeatureFlagsResolver` / `FeatureFlagsContextSync` has
   * completed before redirecting so flags match the current user/org context.
   */
  const { isUserContextIdentified: isLaunchDarklyUserIdentified } =
    useFeatureFlagsProviderContext();

  const profileParams = useSelector(selectProfileParams); // use login role
  const baseProfile = useSelector(selectProfile);
  const profileContextIds = useSelector(selectProfileContextIds);
  const permissionContextIds = useSelector(selectPermissionsContextIds);
  const allowedContexts = useSelector(selectAllowedContexts);
  const { status: profileRequestStatus, errors: profileRequestErrors } =
    useSelector(selectGetProfileOperation);
  const { status: permissionsRequestStatus, errors: permissionsRequestErrors } =
    useSelector(selectGetPermissionsOperation);

  const { status: organizationRequestStatus } = useSelector(
    selectGetOrgDetailsOperation
  );

  const actingProfile = useSelector(selectActingProfile);
  const currentLocale = useSelector(selectCurrentLocale);
  const { profile } = useUser();

  const impersonationHomePath = useMemo(
    () => getImpersonationHomePath(baseProfile, actingProfile),
    [baseProfile, actingProfile]
  );

  const [organizationReady, setOrganizationReady] = useState(false);
  const [loginError, setLoginError] = useState<BDLoginError | null>(null);
  // The failed read login last routed on, so a re-render with the same error
  // neither routes nor logs it twice.
  const routedReadErrorRef = useRef<BDError | null>(null);
  const hasResolvedActingProfile = useRef(false);

  // When the profile lookup 404s the user may still have an org enrollment in
  // flight. Resolve their enrollment status so we can route them to the pending
  // verification screen instead of restarting enrollment. A premature-enrollment
  // session already has a destination, so that lookup can be skipped.
  const {
    data: enrollmentStatusData,
    isSuccess: isEnrollmentStatusSuccess,
    isError: isEnrollmentStatusError,
  } = rtkOrgClient.useGetEnrollmentStatusQuery(
    loginError === BDLoginError.NO_PROFILE && !hasPrematureEnrollmentSession()
      ? undefined
      : skipToken
  );

  const redirectToLogout = useCallback(() => {
    navigate({
      pathname: AppRoutePaths.LOGOUT,
      search: previousRoute.toString(),
    });
  }, [navigate, previousRoute]);

  const routeOnReadError = useCallback(
    (read: LoginRead, error: BDError) => {
      if (routedReadErrorRef.current === error) {
        return;
      }
      routedReadErrorRef.current = error;

      const outcome =
        error.status === 404
          ? LoginReadOutcome.ENROLLMENT
          : error.status === 401
            ? LoginReadOutcome.LOGOUT
            : LoginReadOutcome.SERVICE_UNAVAILABLE;
      logLoginReadDecision({ read, error, outcome });

      if (outcome === LoginReadOutcome.ENROLLMENT) {
        setLoginError(BDLoginError.NO_PROFILE);
      } else if (outcome === LoginReadOutcome.LOGOUT) {
        // The session died between the redirect and the read.
        redirectToLogout();
      } else {
        setLoginError(BDLoginError.SERVICE_ERROR);
      }
    },
    [redirectToLogout]
  );

  useEffect(() => {
    if (!instance || !isMsalInitialized) {
      return;
    }

    if (!hasAnyCachedAccounts && !isAuthenticated) {
      // Root never picks SSO on the user's behalf: the stored `auth_type` is
      // only a record of the last session, so replaying it here traps anyone
      // who once tried employee sign-in on the SSO form for good. Only an
      // explicit `authType` already on the URL carries through.
      const search = new URLSearchParams(location.search);
      const redirectPath = previousRoute.get('redirectPath');
      if (redirectPath) {
        search.set('redirectPath', redirectPath);
      }
      const query = search.toString();

      // The stored SSO `auth_type` already bootstrapped MSAL against the SSO
      // authority, but there is no session to resume and we are about to render
      // the CDI form. Reload rather than navigate so the app re-reads the auth
      // type off the URL and backs the form with the CDI instance, instead of
      // posting customer credentials to the employee authority. `auth_type` is
      // rewritten by MsalManager on that reload, so it needs no cleanup here.
      if (
        authStorage.auth_type.getAuthType() === AUTH_TYPES.SSO &&
        !search.has(AUTH_TYPE_KEY)
      ) {
        window.location.replace(
          `${AppRoutePaths.LOGIN}${query ? `?${query}` : ''}`
        );
        return;
      }

      navigate(
        { pathname: AppRoutePaths.LOGIN, search: query },
        { replace: true }
      );
      return;
    }

    const checkAuth = async () => {
      if (!instance?.getActiveAccount() && !isAuthenticated) {
        redirectToLogout();
        return;
      }

      const res = await refreshTokenHandler();
      if (res?.accessToken) {
        sendTokenToNative(res.accessToken);
      }
    };

    checkAuth();
  }, [
    instance,
    isAuthenticated,
    redirectToLogout,
    hasAnyCachedAccounts,
    isMsalInitialized,
    navigate,
    previousRoute,
    location.search,
  ]);

  // A profile and its permissions are created together at enrollment, so a user
  // without a profile has no permissions either and that read fails too. The
  // profile read decides; a permissions failure only routes once the profile is
  // known to exist, whichever of the two lands first.
  useEffect(() => {
    const profileError = profileRequestErrors?.[0];
    if (profileError) {
      routeOnReadError(LoginRead.PROFILE, profileError);
      return;
    }

    const permissionsError = permissionsRequestErrors?.[0];
    if (permissionsError && profile) {
      routeOnReadError(LoginRead.PERMISSIONS, permissionsError);
    }
  }, [
    routeOnReadError,
    profileRequestErrors,
    permissionsRequestErrors,
    profile,
  ]);

  useEffect(() => {
    if (profileParams.id) {
      addTelemetryContext('user', {
        id: profileParams.id,
        role: profileParams.role,
        organization: profileParams.organization,
        hub: profileParams.hub,
        fleet: profileParams.fleet,
      });
    }
  }, [addTelemetryContext, profileParams]);

  useEffect(() => {
    if (profileRequestStatus !== BDRequestStatus.SUCCEEDED) {
      return;
    }
    if (
      profileParams?.role === Role.ORG_MANAGER &&
      profileParams?.status === ActivationStatus.ACTIVE &&
      profileContextIds?.organizationsId
    ) {
      dispatch(
        getOrganizationDetails({
          organizationsId: profileContextIds?.organizationsId,
        })
      );
    } else {
      setOrganizationReady(true);
    }
  }, [
    profileRequestStatus,
    profileParams?.role,
    profileParams?.status,
    profileContextIds?.organizationsId,
    dispatch,
  ]);

  useEffect(() => {
    setOrganizationReady(
      (previousValue) =>
        previousValue || organizationRequestStatus === BDRequestStatus.SUCCEEDED
    );
  }, [organizationRequestStatus]);

  const organizationId =
    !profileParams.role || profileParams.role === Role.GMF_ADVISOR
      ? undefined
      : (profile?.organization?.id ?? actingProfile?.organization?.id);
  const { data: organizationDetailsData, isFetching: isOrgDetailsFetching } =
    rtkOrgClient.useGetOrgByIdQuery(
      organizationId ? { id: organizationId } : skipToken
    );

  // Until the SDK loads, the `heap-init.js` stub reports no identity even after
  // `identify` is queued, so the identity check alone would let a later run start
  // a second session. Later runs for the same profile only refresh its properties,
  // which picks up an organization that resolved after the first identify.
  const heapIdentifiedProfileIdRef = useRef<string | null>(null);

  useEffect(() => {
    const isIdentifiedHere =
      !!profile?.id && heapIdentifiedProfileIdRef.current === profile.id;

    let identity = isIdentifiedHere ? null : window.heap?.getIdentity();
    if (identity?.includes?.('cdi:')) {
      window.heap.resetIdentity();
      identity = null;
    }

    if (!profile?.id || !profileParams.role || identity !== null) {
      return;
    }
    if (organizationId && isOrgDetailsFetching) {
      return;
    }

    const heapUserProperties = buildHeapUserProperties(
      profile,
      organizationDetailsData?.result,
      currentLocale
    );
    if (isIdentifiedHere) {
      window.heap.addUserProperties(heapUserProperties);
      return;
    }

    window.heap.identify(profile.id);
    heapIdentifiedProfileIdRef.current = profile.id;
    window.heap.addUserProperties(heapUserProperties);

    dispatchHeapEvent(SESSION_START_EVENT_BY_PLATFORM[getHeapPlatform()], {
      role: heapUserProperties.role,
      isMigrated: heapUserProperties.isMigrated,
    });
  }, [
    profile,
    profileParams.role,
    isOrgDetailsFetching,
    organizationDetailsData,
    organizationId,
    currentLocale,
  ]);

  // Resolves which profile the user acts as once the reads the `UserProvider`
  // issued have landed in the store. The ref keeps it to a single pass: the
  // permissions no longer arrive here as a promise that resolves once, and the
  // acting profile it settles on is itself part of the state it reads.
  useEffect(() => {
    if (
      !isAuthenticated ||
      !profileParams.id ||
      profileRequestStatus !== BDRequestStatus.SUCCEEDED ||
      permissionsRequestStatus !== BDRequestStatus.SUCCEEDED ||
      hasResolvedActingProfile.current
    ) {
      return;
    }
    hasResolvedActingProfile.current = true;

    if (baseProfile?.role === Role.GMF_ADVISOR) {
      dispatch(setActingProfile());
      return;
    }
    if (!hasPersistedActingOrgContext(baseProfile, actingProfile)) {
      dispatch(setActingProfile());
      return;
    }

    const orgId = getActingOrgId(actingProfile);
    if (!orgId) {
      return;
    }

    dispatch(fetchEntitlements({ orgId }));

    if (allowedContexts?.[orgId]) {
      dispatch(setActingProfile({ organizationsId: orgId }));
      return;
    }

    dispatch(getAllowedContexts({ organizationsId: orgId }))
      .unwrap()
      .then(() => {
        dispatch(setActingProfile({ organizationsId: orgId }));
      })
      .catch(() => {
        dispatch(setActingProfile());
      });
  }, [
    isAuthenticated,
    profileParams.id,
    baseProfile,
    actingProfile,
    allowedContexts,
    profileRequestStatus,
    permissionsRequestStatus,
    dispatch,
  ]);

  // Fetch org hierarchy
  useEffect(() => {
    const { organizationsId, hubsId, fleetsId } = profileContextIds || {};
    if (
      profileParams.role &&
      !CONTEXT_EXCLUSIONS.includes(profileParams.role) &&
      organizationsId &&
      profileParams.status === ActivationStatus.ACTIVE
    ) {
      dispatch(
        getAllowedContexts({
          organizationsId,
          hubsId,
          fleetsId,
        })
      );
    }
  }, [dispatch, profileParams.role, profileContextIds, profileParams.status]);

  // Fetch org entitlements
  useEffect(() => {
    const { organizationsId } = profileContextIds || {};
    if (
      profileParams.role !== Role.GMF_ADVISOR &&
      organizationsId &&
      profileParams.status === ActivationStatus.ACTIVE
    ) {
      dispatch(
        fetchEntitlements({
          orgId: organizationsId,
        })
      );
    }
  }, [dispatch, profileContextIds, profileParams.status, profileParams.role]);

  useEffect(() => {
    //Only want to redirect to terms and conditions if the user's status is pending
    //and the invite is not in a cancelled or expired state
    //Note: Users without an invitation typically are legacy users that have been whitelisted
    if (
      profileParams.status === ActivationStatus.PENDING &&
      profileRequestStatus === BDRequestStatus.SUCCEEDED &&
      permissionsRequestStatus === BDRequestStatus.SUCCEEDED
    ) {
      //Need to redirect users to an error page for cancelled and expired invitations
      if (
        profileParams.invite?.status === InvitationStatus.CANCELLED ||
        profileParams.invite?.status === InvitationStatus.EXPIRED
      ) {
        const path =
          profileParams.invite?.status === InvitationStatus.CANCELLED
            ? AppRoutePaths.CANCELLED_INVITE
            : AppRoutePaths.EXPIRED_INVITE;
        navigate(path);
      } else {
        const path = `/users/${profileParams?.id}/terms-and-conditions`;
        navigate(path);
      }
    }
  }, [
    navigate,
    permissionsRequestStatus,
    profileParams?.id,
    profileParams.invite?.status,
    profileParams.status,
    profileRequestStatus,
  ]);

  useEffect(() => {
    if (
      profileParams.status === ActivationStatus.ACTIVE &&
      profileRequestStatus === BDRequestStatus.SUCCEEDED &&
      permissionsRequestStatus === BDRequestStatus.SUCCEEDED &&
      isLaunchDarklyUserIdentified &&
      organizationReady
    ) {
      if (profileParams.role === Role.GMF_ADVISOR) {
        navigate(
          getGmfAdvisorDestination(
            pendingRedirectPath ?? '/organizations',
            isGmfRolloutEnabled(
              getFlag(ADVISOR_TOOL_GMF_FLAG),
              isLaunchDarklyUserIdentified
            )
          ),
          { replace: true }
        );
        return;
      }
      if (pendingRedirectPath) {
        navigate(pendingRedirectPath, { replace: true });
        return;
      }

      const search = new URLSearchParams(location.search);
      const path = search.get('redirectPath');
      if (path && path !== '/') {
        navigate(path, { replace: true });
        return;
      }

      const defaultPath =
        impersonationHomePath ??
        getDefaultRoutePath(
          profileParams?.role,
          permissionContextIds || {},
          actingProfile
        );

      if (location.pathname !== defaultPath) {
        navigate(defaultPath);
      }
    }
  }, [
    profileRequestStatus,
    permissionsRequestStatus,
    navigate,
    profileParams,
    permissionContextIds,
    actingProfile,
    organizationReady,
    isLaunchDarklyUserIdentified,
    location.search,
    location.pathname,
    pendingRedirectPath,
    impersonationHomePath,
    getFlag,
  ]);

  /*
  TODO: Remove this effect and its slice once '34205-Charging-Schedule-Config' is removed.
  It is the only feature flag that is used deep in the state
  */
  useEffect(() => {
    if (featureFlags) {
      dispatch(setEffectiveFeatures(featureFlags));
    }
  }, [dispatch, featureFlags]);

  useEffect(() => {
    if (loginError === BDLoginError.SERVICE_ERROR) {
      navigate(AppRoutePaths.SERVICE_UNAVAILABLE);
    }
  }, [navigate, loginError]);

  // Route users without a profile: a premature-enrollment session goes to the
  // migration not complete page. Otherwise, an enrollment awaiting verification (org in
  // PENDING/ENROLLING) goes to the pending verification screen, and everyone
  // else starts enrollment. Fall back to enrollment if the status lookup fails
  // so login is never blocked.
  useEffect(() => {
    if (loginError !== BDLoginError.NO_PROFILE) {
      return;
    }

    if (hasPrematureEnrollmentSession()) {
      navigate(findRoute(RouteName.MIGRATION_NOT_COMPLETE).path);
      return;
    }

    if (!isEnrollmentStatusSuccess && !isEnrollmentStatusError) {
      return;
    }

    const organizationStatus =
      enrollmentStatusData?.result?.organizationStatus ?? '';
    const isPendingEnrollment =
      isEnrollmentStatusSuccess &&
      PENDING_ENROLLMENT_ORG_STATUSES.includes(organizationStatus);
    const path = isPendingEnrollment
      ? PENDING_VERIFICATION_PATH
      : AppRoutePaths.BUSINESS_ENROLLMENT;
    navigate(path);
  }, [
    navigate,
    loginError,
    isEnrollmentStatusSuccess,
    isEnrollmentStatusError,
    enrollmentStatusData,
  ]);

  if (isMsalInitialized && !hasAnyCachedAccounts && !isAuthenticated) {
    return null;
  }

  return (
    <Box data-testid="loading" m={0}>
      <BDPageSpinner />
    </Box>
  );
};

export default InboundRedirect;
